import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook para capturar entrada de escáner de código de barras USB.
 * 
 * Los escáneres USB actúan como teclado: "teclean" el código rápidamente
 * y envían Enter al final. Este hook detecta esa entrada rápida
 * y la distingue de la escritura normal del usuario.
 * 
 * @param {Function} onScan - Callback con el código escaneado
 * @param {Object} options
 * @param {boolean} options.enabled - Si el listener está activo (default: true)
 * @param {number} options.maxDelay - Máx. ms entre teclas para considerar escaneo (default: 50)
 * @param {number} options.minLength - Largo mínimo del código (default: 4)
 */
const useBarcodeScanner = (onScan, options = {}) => {
  const {
    enabled = true,
    maxDelay = 50,
    minLength = 4,
  } = options;

  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const timeoutRef = useRef(null);
  const onScanRef = useRef(onScan);

  // Mantener la ref actualizada sin causar re-renders
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const resetBuffer = useCallback(() => {
    bufferRef.current = '';
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      const now = Date.now();
      const activeEl = document.activeElement;
      const tagName = activeEl?.tagName?.toLowerCase();

      // Si el usuario está en un input/textarea/select, solo interceptar
      // si la entrada es lo suficientemente rápida (viene del escáner)
      const isInFormField = tagName === 'input' || tagName === 'textarea' || tagName === 'select';

      // Si pasó mucho tiempo desde la última tecla, reiniciar buffer
      if (now - lastKeyTimeRef.current > maxDelay && bufferRef.current.length > 0) {
        bufferRef.current = '';
      }

      lastKeyTimeRef.current = now;

      // Enter = fin de escaneo
      if (e.key === 'Enter') {
        if (bufferRef.current.length >= minLength) {
          const code = bufferRef.current.trim();
          e.preventDefault();
          e.stopPropagation();

          // Limpiar el input si el escáner escribió ahí
          if (isInFormField && activeEl.value) {
            // Remover el código escaneado del valor del input
            const inputVal = activeEl.value;
            if (inputVal.endsWith(code)) {
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype, 'value'
              )?.set;
              if (nativeInputValueSetter) {
                nativeInputValueSetter.call(activeEl, inputVal.slice(0, -code.length));
                activeEl.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
          }

          onScanRef.current(code);
          bufferRef.current = '';
          return;
        }
        bufferRef.current = '';
        return;
      }

      // Solo caracteres alfanuméricos y guiones
      if (e.key.length === 1 && /[a-zA-Z0-9-]/.test(e.key)) {
        bufferRef.current += e.key;

        // Timeout de seguridad: si no llega Enter, limpiar
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          bufferRef.current = '';
        }, 300);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, maxDelay, minLength]);

  return { resetBuffer };
};

export default useBarcodeScanner;
